import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaPaw } from "react-icons/fa";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ABOUT_IMAGE, OUTDOOR_IMAGE } from "@/lib/site";
import { FadeUp, MaskLines } from "@/components/Reveal";

// born 23/12/2005 — age updates automatically
const getAge = () => {
  const birth = new Date(2005, 11, 23);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age;
};

const SPECS = [
  { label: "Ears", value: "Expressive ×2" },
  { label: "Vision", value: "Wide. Always wide." },
  { label: "Paws", value: "Four, all good boys" },
  { label: "Tail", value: "Wag-rate: 500/h" },
  { label: "Fur", value: "100% fluff, 0% chill" },
  { label: "Power", value: "Beer-powered" },
  { label: "Second home", value: "VRChat" },
  { label: "Main game", value: "Escape From Tarkov" },
  { label: "Weekend ops", value: "Drinks with friends" },
];

const STATS = [
  { label: "Fluffiness", pct: 100 },
  { label: "Barking", pct: 100 },
  { label: "Zoomies", pct: 110 },
  { label: "Chill", pct: 30 },
  { label: "Raid survival", pct: 40 },
];

const FAQ = [
  {
    q: "Can I get a hug?",
    a: "Absolutely — ask first. Husko runs on consent, pets and compliments.",
  },
  {
    q: "Why 'TheHuskoBiscuit'?",
    a: "I once needed to come up with a name for VRChat, seeing as my old nickname 'Huskyy' was too common — and that name transferred over to all my other accounts :3",
  },
  {
    q: "Can I use Husko's photos?",
    a: "You can use all my pictures as long as you tag me / give me credit.",
  },
  {
    q: "Does Husko bite?",
    a: "Only if you ask nicely.",
  },
];

export default function About() {
  return (
    <div className="pt-16">
      <section className="hero-glow relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-husko-cyan">About the husko</p>
          <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskLines baseDelay={0.1} lines={["Who's the", <span key="h" className="text-gradient-husky">husko?</span>]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-6 max-w-lg text-base text-slate-300 sm:text-lg"
          >
            Husko. TheHuskoBiscuit. All the same fluff — welcome to the cozy
            corner.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <FadeUp>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-1.5 rounded-[2.2rem] bg-gradient-to-br from-husko-purple via-husko-magenta to-husko-yellow opacity-60 blur-md" />
              <img
                src={ABOUT_IMAGE}
                alt="Husko the husky fursuiter posing"
                className="relative aspect-[4/5] w-full rounded-[2rem] object-cover"
                data-testid="about-portrait-image"
              />
              <div className="absolute -bottom-5 left-6 rotate-[-4deg] rounded-2xl border border-husko-magenta/50 bg-void/85 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-husko-magenta backdrop-blur">
                That's Felix
              </div>
            </div>
          </FadeUp>
          <div>
            <FadeUp>
              <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
                The origin <span className="text-gradient-husky">awoo</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-6 space-y-5 leading-relaxed text-slate-300">
                <p>
                  I'm a furry who grew up on the internet — most of my life happened in
                  the online furry community: VRChat hangouts, Discord dens, all of it.
                  The fursuit was always the dream, the "one day" I kept putting off.
                </p>
                <p>
                  Then came MFF 2024 in Chicago — my first con — and right after landing
                  my first fulltime job, I gave in and ordered the suit. April 16: the
                  partial arrived, and Felix was real. The bodysuit landed a week before
                  EF30, completing the boy — and I've loved him ever since.
                </p>
                <p>
                  Suit aside: I'm a femboy who mains Escape From Tarkov, hops into
                  VRChat now and then, streams to a small-but-mighty Twitch crew —
                  sharing funny moments with friends along the way — and loves drinking
                  with friends. By day I'm an IT nerd studying for my Bachelor's in
                  Computer Science.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-slate-300">
                {/* pronouns + age: born 23/12/2005, age computes automatically */}
                {[
                  `He/him & she/her`,
                  `${getAge()} yrs old`,
                  "Femboy",
                  "Tarkov main",
                  "VRChat now & then",
                  "Twitch streamer",
                  "CS student",
                  "IT nerd",
                  "Drinking buddy",
                ].map((chip) => (
                  <span key={chip} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-den px-3.5 py-2">
                    <FaPaw size={10} className="text-husko-cyan" /> {chip}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-husko-yellow">Hardware review</p>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
                Suit <span className="text-gradient-husky">specs</span>
              </h2>
              <p className="mt-4 max-w-sm text-slate-400">
                Factory settings, straight from the den. All parts certified fluffy.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-8 overflow-hidden rounded-3xl border border-white/10">
                <img
                  src={OUTDOOR_IMAGE}
                  alt="Husko outdoors"
                  className="aspect-[4/3] w-full object-cover"
                  data-testid="about-outdoor-image"
                />
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {SPECS.map((spec, i) => (
                <FadeUp key={spec.label} delay={i * 0.06}>
                  <div
                    className="h-full rounded-2xl border border-white/10 bg-den p-6 transition-colors hover:border-husko-cyan/40"
                    data-testid={`about-spec-card-${spec.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-husko-cyan">{spec.label}</p>
                    <p className="mt-3 font-display text-base font-bold text-slate-100">{spec.value}</p>
                  </div>
                </FadeUp>
              ))}
            </div>

            <FadeUp delay={0.15}>
              <div className="mt-8 rounded-3xl border border-white/10 bg-den p-8" data-testid="about-stats-card">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-husko-magenta">
                  Character stats — verified by peer review (the peer was also a husky)
                </p>
                <div className="mt-6 space-y-5">
                  {STATS.map((stat, i) => (
                    <div key={stat.label}>
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="font-display font-bold text-slate-200">{stat.label}</span>
                        <span className="font-mono text-xs text-husko-cyan">
                          {stat.pct > 100 ? "off the chart" : `${stat.pct}/10`}
                        </span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-void">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${Math.min(stat.pct, 100)}%` }}
                          viewport={{ once: true, amount: 0.6 }}
                          transition={{ duration: 1.1, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                          className="h-full rounded-full bg-gradient-to-r from-husko-cyan via-husko-purple to-husko-magenta"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <FadeUp>
          <h2 className="text-center font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
            Frequently asked <span className="text-gradient-husky">awoos</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <Accordion type="single" collapsible className="mt-10">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border-white/10" data-testid={`about-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-base font-bold text-slate-100 hover:text-husko-cyan hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="mt-14 rounded-3xl border border-white/10 bg-gradient-to-br from-husko-purple/20 via-den to-husko-cyan/10 p-10 text-center">
            <h3 className="font-display text-xl font-extrabold text-slate-50 sm:text-2xl">
              Enough reading. Look at the fluff.
            </h3>
            <Link
              to="/gallery"
              data-testid="about-cta-gallery"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-husko-cyan to-husko-magenta px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-void transition-transform hover:scale-[1.03]"
            >
              Open the gallery
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
